<template>
  <div class="pa-4">
    <div v-if="loading">
      <v-skeleton-loader type="card ,article, actions"></v-skeleton-loader>
    </div>
    <div row wrap v-else-if="!loading" class="pa-0">
      <v-card elevation="0" class="mt-4">
        <v-row v-if="videoId == 'new_admin_video'">
          <v-col class="pb-0 mb-2" cols="12" sm="12">
            <v-file-input dense v-model="videoToUpload" accept="video/*" :label="$t('message.upload_video')" outlined
              prepend-icon="" prepend-inner-icon="mdi-video-plus-outline" hide-details
              :disabled="uploading ? true : false">
            </v-file-input>
            <div class="text-caption">
              <v-icon size="15" class="ml-1 mr-1">mdi-alert-outline</v-icon>
              {{ $t('message.video_recording_warning') }}
            </div>
            <div class="text-caption">
              <v-icon size="15" class="ml-1 mr-1">mdi-alert-outline</v-icon>
              {{ $t('message.no_support_hd_videos') }}
            </div>
          </v-col>
        </v-row>
        <v-card-text v-if="uploading">
          <v-progress-linear buffer-value="5" color="success" height="15" stream :value="uploadPercentage">
          </v-progress-linear>
        </v-card-text>
        <v-row v-else class="mt-2">
          <v-col class="pb-0" cols="12" sm="12">
            <v-text-field outlined dense v-model="video.title" :label="$t('message.title')"></v-text-field>
          </v-col>
          <v-col class="pb-0" cols="12" sm="12" md="6">
            <category-combo v-model="video.category" :label="$t('message.category')" />
          </v-col>
          <v-col class="pb-0" cols="12" sm="12" md="6">
            <TagsCombo v-model="video.tags" :label="$t('message.video_tags') + ' (' + $t('message.optional') + ')'" />
          </v-col>
          <v-col class="pb-0" cols="12" sm="12">
            <v-textarea v-model="video.description" outlined rows="3" :label="$t('message.description')"></v-textarea>
          </v-col>
          <v-col v-if="videoId != 'new_admin_video'" class="pb-0 mb-2" cols="12" sm="10">
            <v-file-input @change="uploadNewImage" dense v-model="videoImageToUpload" :rules="mediaRules"
              accept="image/jpeg, image/png" :label="$t('message.update_image')" outlined prepend-icon=""
              prepend-inner-icon="mdi-image-plus-outline" :disabled="uploading ? true : false">
            </v-file-input>
          </v-col>
          <v-col v-if="videoId != 'new_admin_video'" class="pb-0 mb-2" cols="12" sm="2">
            <ImageDisplay v-if="!uploadingImage" class="video-thumbnail" :src="video.thumbnail"></ImageDisplay>
            <v-progress-circular v-else size="30" indeterminate color="secondary"></v-progress-circular>
          </v-col>
          <v-col class="pb-0" cols="12" sm="12">
            <v-switch v-if="videoId == 'new_admin_video'" v-model="acknowledgement"
              :label="$t('message.video_rights')"></v-switch>
            <v-switch v-model="video.private" :label="$t('message.private')"></v-switch>
            <v-switch v-show="video.private ? false : true" v-if="videoId == 'new_admin_video'"
              v-model="video.notifyMembers" :label="$t('message.notify_members')"></v-switch>
          </v-col>
        </v-row>
        <v-card-text class="pa-0" v-if="videoId != 'new_admin_video'">
          <v-responsive v-if="video.ready">
            <video-display style="max-height:300px" :video="video"></video-display>
          </v-responsive>
          <div v-else-if="video.encodingError" class="pa-2 ma-2 text-center">
            <v-icon size="100" color="error">mdi-close-circle-outline</v-icon>
            <div>
              {{ $t('message.encoding_error') }}
            </div>
          </div>
          <v-responsive v-else class="pa-2 ma-2 text-center">
            <v-btn @click="refreshData()" outlined color="primary" block>{{ $t('message.refresh') }}</v-btn>
            <v-progress-circular size="40" indeterminate color="secondary" class="mt-2"></v-progress-circular>
            {{ $t('message.video_still_processing') }}
          </v-responsive>
        </v-card-text>
        <v-list v-if="videoId != 'new_admin_video'">
          <v-list-item>
            <v-list-item-action>
              <v-icon>mdi-account-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ video.user.fName }} {{ video.user.lName }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t("message.uploaded_by") }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-action>
              <v-icon>mdi-calendar-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ $d(new Date(video.uploaded), 'long', $i18n.locale) }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t("message.uploaded_on") }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-action>
              <v-icon>mdi-eye-circle-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ video.views ? video.views.length : 0 }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t("message.views") }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="video.views && video.views.length > 0">
              <v-btn @click="getViewers()" small elevation="0">{{ $t("message.view_more") }}</v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-card-actions class="mt-2">
          <v-btn outlined v-if="videoId == 'new_admin_video'" small elevation="0"
            :disabled="!video.title || !videoToUpload || !video.category || !acknowledgement || uploading"
            @click="submitNewVideo()" color="primary">
            <v-icon v-if="uploading" class="rotate">mdi-refresh</v-icon>
            {{ $t('message.add') }}
          </v-btn>
          <v-btn outlined v-else small elevation="0" @click="updateVideo()" color="primary">
            {{ $t('message.update') }}
          </v-btn>
          <v-btn small elevation="0" @click="goBack()" class="outlined-btn">
            {{ $t('message.back') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn small elevation="0" v-if="videoId != 'new_admin_video'" @click="displayDeleteVideoConfirm()"
            color="error">
            {{ $t('message.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
    <div v-else>
      <h1>You should NOT be here... sorry, brou</h1>
    </div>
    <v-dialog v-model="deleteVideoDialog" max-width="50%">
      <v-card>
        <v-card-title class="text-title">
          {{ $t('message.delete_video') }}
        </v-card-title>
        <v-card-text class="text-subtitle-1">
          {{ $t('message.are_you_sure') }}
        </v-card-text>
        <v-card-actions>
          <v-btn small elevation="0" color="error" @click.stop="deleteVideo()">{{ $t('message.delete') }}</v-btn>
          <v-btn class="outlined-btn" small elevation="0" @click.stop="deleteVideoDialog = false">{{ $t('message.close')
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog class="pa-0" v-model="viewsDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card outlined class="pa-0">
        <v-toolbar>
          <v-toolbar-title>
            {{ $t('message.views') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn elevation="0" icon dark @click="viewsDialog = false">
            <v-icon>mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <v-skeleton-loader v-if="loadingViews" type="list-item-avatar, divider, list-item-avatar"></v-skeleton-loader>
        <v-list two-line subheader v-if="views.length > 0">
          <template v-for="(i, index) in views">
            <v-list-item :key="index + '_' + i._id" @click="goToMember(i._id, i.displayName)">
              <v-list-item-avatar>
                <ImageDisplay v-if="i.userImageSmall" :src="i.userImageSmall" :title="i.displayName" />
                <img v-else :src="assets('default-profile-avatar.png')" :title="i.displayName" />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  <span v-html="i.displayName"></span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider v-if="index + 1 < views.length" :key="index"></v-divider>
          </template>
        </v-list>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from "@/services/Api2";
import TagsCombo from "@/components/inputs/TagsCombo.vue";
import CategoryCombo from "@/components/inputs/VideoCategoryCombo.vue";
import VideoDisplay from "@/components/displays/VideoDisplay.vue";
//Needed to reencode .mov (quicktime) videos

export default {
  data() {
    return {
      addingNew: false,
      vm: this,
      videoId: this.$route.params.videoId,
      loading: true,
      video: null,
      views: [],
      viewsDialog: false,
      isManager: false,
      uploading: false,
      errorMessage: null,
      videoToUpload: null,
      videoImageToUpload: null,
      uploadingImage: false,
      acknowledgement: false,
      deleteVideoDialog: false,
      privateVideo: false,
      sendNotifications: false,
      bjjlinkVideoId: null,
      userTags: null,
      uploadPercentage: 0,
      loadingViews: false,
      mediaRules: [
        (v) => !v || v.size < 9000000 || "Image should be less than 9MB",
      ],
    };
  },
  created() {
    //get events
    if (this.videoId == "new_admin_video") {
      this.loading = false;
      this.addingNew = true;
      this.video = {
        user: this.$store.state.user._id,
        private: false,
        notifyMembers: true,
      };
    } else {
      this.loading = true;
      this.video = this.getVideo();
    }
  },
  components: {
    TagsCombo,
    CategoryCombo,
    VideoDisplay,
  },
  methods: {
    refreshData() {
      this.getVideo();
    },
    getViewers() {
      this.loadingViews = true;
      this.viewsDialog = true;
      Api()
        .get("videos/admins/" + this.$store.state.managedAcademy + '/video/' + this.videoId + '/views')
        .then(response => {
          const data = response.data;
          if (data) {
            this.views = data.data;
            this.loadingViews = false;
          }
        });
    },
    async uploadNewImage() {
      const vm = this;
      this.uploadingImage = true;
      let formData = new FormData();
      const fileName = this.cleanAndFormatFilename(this.videoImageToUpload.name);
      formData.append("image", this.videoImageToUpload, fileName);
      Api(this.longTimeout()).post("content/image/admin", formData)
        .then(res => {
          if (res) {
            setTimeout(function () {
              const newImage = res.data.location;
              vm.video.thumbnail = newImage;
              vm.uploadingImage = false;
              vm.showAlert(
                "success",
                this.$t('message.uploaded_remember_udpate')
              );
            }, 4000);
          }
        })
        .catch(err => {
          this.uploadingImage = false;
          if (err)
            this.showAlert('error', this.$t('message.oops_not_working'));
        });
    },
    async submitNewVideo() {
      let formData = new FormData();
      try {
        let uploadedVideoLocation = null;
        let uploadedVideoFileId = null;
        let originalFileName = null;
        if (this.videoToUpload) {
          this.uploading = true;
          const vm = this;
          const d = new Date();
          const millis = d.getTime();
          let videoExtension = this.videoToUpload.type.toLowerCase().split('/')[1];
          /*
          if (videoExtension == 'quicktime') {
            videoExtension = 'mov';
            this.uploading = false;
            this.showAlert('error', 'Unsupported QuickTime video format detected. macOS screen recordings can cause issues during processing. Please upload standard MP4 videos instead.');
            return;
          }
          */
          const blob = new Blob([this.videoToUpload], { type: this.videoToUpload.type });
          //formData.append("video", this.videoToUpload);
          originalFileName = this.cleanAndFormatFilename(this.videoToUpload.name, 'video');
          formData.append("video", blob, 'bjjlink-user-admin-uploaded-video-' + millis + '.' + videoExtension);
          const videoUploadedResponse = await Api(this.longTimeout(10)).post("admins/managedacademy/" +
            this.$store.state.managedAcademy +
            "/video/convert", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: function (progressEvent) {
              const percent = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
              if (percent >= 97)
                vm.uploadPercentage = 97;//Stay on 97
              else
                vm.uploadPercentage = percent;
            },
          });
          if (videoUploadedResponse && videoUploadedResponse.data) {
            uploadedVideoLocation = videoUploadedResponse.data.location;
            uploadedVideoFileId = videoUploadedResponse.data.fileId;
            const data = {
              title: this.video.title,
              description: this.video.description,
              category: this.video.category,
              tags: this.video.tags,
              userTags: this.userTags,
              private: this.video.private,
              thumbnail: videoUploadedResponse.data.thumbnail,
              originalFileName: originalFileName,
              fileUrl: uploadedVideoLocation,
              fileId: uploadedVideoFileId,
              notifyMembers: this.video.private ? false : this.video.notifyMembers,
              acknowledgement: this.acknowledgement
            };
            Api().post("admins/academy/" +
              this.$store.state.managedAcademy +
              "/video/add", data)
              .then(res => {
                if (res) {
                  this.navigateTo({ name: "videos" });
                }
              })
              .catch(err => {
                this.uploading = false;
                if (err)
                  this.showAlert('error', this.$t('message.oops_not_working'));
              });
          } else {
            this.showAlert('error', this.$t('message.oops_not_working'));
          }
        }
      } catch (error) {
        this.showAlert('error', this.$t('message.oops_not_working'));
      }
    },
    clearParameters() {
      this.video.description = null;
      this.video.tags = [];
      this.video.category = null;
      this.video.title = null;
      this.uploading = false;
      this.acknowledgement = false;
    },
    goBack() {
      this.$router.go(-1);
    },
    goodToProceed() {
      if (
        this.video &&
        this.video.title &&
        this.video.category
      ) {
        if (this.videoId == "new_admin_video")
          return false;
        else return true;
      } else {
        return false;
      }
    },
    getVideo() {
      Api()
        .get("videos/admins/" + this.$store.state.managedAcademy + '/get/' + this.videoId)
        .then(response => {
          const data = response.data;
          if (data) {
            this.video = data.video;
            this.loading = false;
          }
        })
        .catch(err => {
          if (err)
            this.showAlert(
              "error",
              this.$t('message.oops_not_working')
            );
        });
    },
    updateVideo() {
      if (this.goodToProceed()) {
        Api().patch('videos/admins/' + this.$store.state.managedAcademy + '/update/' + this.videoId, this.video)
          .then(data => {
            if (data) {
              this.showAlert(
                "success",
                this.$t('message.updated')
              );
            }
          })
          .catch(err => {
            if (err)
              this.showAlert(
                "error",
                this.$t('message.oops_not_working')
              );
          });
      }
    },
    displayDeleteVideoConfirm() {
      this.deleteVideoDialog = !this.deleteVideoDialog
    },
    async deleteVideo() {
      const data = await Api().delete('videos/admins/' + this.$store.state.managedAcademy + '/video/' + this.videoId)
      if (data) {
        this.navigateTo({ name: 'videos' });
      }
    }
  }
};
</script>

<style scoped>
.video-thumbnail {
  width: 80px
}
</style>
